import { Observable } from "rxjs";
import { tap, takeUntil } from "rxjs/operators";
import { getGMapRandomColor, getScoreCardQuadrantData } from "./utils";
import { WidgetMetricDialogComponent } from "../../widget-metric-dialog/widget-metric-dialog.component";
import { DeletePopupComponent } from "../../../delete-popup/delete-popup.component";

export function getProjectMetrics(this: any) {
  this.projectService
    .getProjectMetrics(this.projectId)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((res: any) => {
      this.selectedMetrics = res.data;
    });
}

export function performSearch(this: any, filterValues: any): void {
  this.frequency = filterValues.frequency;
  let startDate = this.datePipe.transform(filterValues.startDate, "YYYY-MM-dd");
  let endDate = this.datePipe.transform(filterValues.endDate, "YYYY-MM-dd");
  if (!this.showTrendView) {
    const currentDate = this.datePipe.transform(new Date(), "yyyy-MM-dd");
    startDate = currentDate;
    endDate = currentDate;
  }
  getTrendData.bind(this)(this.frequency, startDate, endDate);
}

export function getTrendData(
  this: any,
  freq: any = null,
  startDate: any = null,
  endDate: any = null
) {
  let qParam = "";
  if (freq) {
    qParam += `&frequency=${freq}`;
  }
  if (startDate) {
    qParam += `&start_date=${startDate}`;
  }
  if (endDate) {
    qParam += `&end_date=${endDate}`;
  }
  this.widgetsLoaded = false;
  this.scorecardService
    .getWidgetTrend(this.currentWidget.id, qParam)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (res: any) => {
        this.widgetsLoaded = true;
        this.widgetTrendData = res;
        this.trendTableColumns =
          this.widgetTrendData?.metrics[0]?.metric_data?.map(
            (val: any) => val.period
          );
        this.trendTableColumns.unshift("Unit");
        this.trendTableColumns.unshift("Metric");
        if (this.showTrendView) {
          this.trendChartOptions = getTrendChartData.bind(this)(
            this.widgetTrendData,
            freq
          );
        } else {
          this.pointInTimeChartOptions = getPointInTimeChartData.bind(this)(
            this.widgetTrendData
          );
        }
      },
      (error: any) => {
        this.widgetsLoaded = true;
      }
    );
}

export function addBgColorToWidgets(this: any) {
  this.widgetsList.forEach((element: any) => {
    element.color = getGMapRandomColor();
  });
}

export function getRecommenededWidgets(this: any) {
  this.widgetsLoaded = false;
  this.scorecardService
    .getRecommendedWidgets(this.projectId)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((res: any) => {
      this.widgetsList = res;
      addBgColorToWidgets.bind(this)();
      this.widgetsLoaded = true;
    });
}

export function getWidgets(this: any) {
  this.widgetsLoaded = false;
  this.scorecardService
    .getWidgets(
      this.projectId,
      this.portfolioId,
      this.subportfolioId,
      this.programId,
      this.vendorId
    )
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((res: any) => {
      this.widgetsLoaded = true;
      this.widgetsList = res;
      addBgColorToWidgets.bind(this)();
    });
}

export function getMetrics(this: any): Observable<any> {
  this.widgetsLoaded = false;
  let projectId: any = null;
  if (
    (this.pageType != "projectLevel" && this.projectId) ||
    this.isCreateWidget ||
    this.isWidgetEditMode
  ) {
    projectId = this.projectId;
  }
  return this.scorecardService.getMetrics(projectId).pipe(
    tap((res: any) => {
      this.metricsList = res.data;
      this.widgetsLoaded = true;
    })
  );
}

export function getCreateWidgetPayload(this: any) {
  let payload = this.widgetCreateForm.value;
  payload["metric_ids"] = this.selectedMetrics.map((metric: any) => metric.id);
  if (this.projectId) {
    payload["type"] = "project";
    payload["project_id"] = this.projectId;
  } else if (this.programId) {
    payload["type"] = "program";
    payload["program_id"] = this.programId;
  } else if (this.subportfolioId) {
    payload["type"] = "sub_portfolio";
    payload["sub_portfolio_id"] = this.subportfolioId;
  } else if (this.portfolioId) {
    payload["type"] = "portfolio";
    payload["portfolio_id"] = this.portfolioId;
  } else {
    payload["type"] = "all_of_it";
  }

  if (this.vendorId) {
    payload["vendor_id"] = this.vendorId;
  }
  return payload;
}

export function handleMetricsDialog(this: any, isProjectLevel: any) {
  const dialog = this.dialog.open(WidgetMetricDialogComponent, {
    width: "1000px",
    height: "80vh",
    disableClose: true,
    autoFocus: false,
    data: {
      metricsList: this.metricsList,
      selectedMetrics: this.selectedMetrics.map((m: any) => m.id),
      isProjectLevelMetric: isProjectLevel,
    },
  });

  dialog.afterClosed().subscribe((data: any) => {
    if (data) {
      this.selectedMetrics = this.metricsList.filter((metric: any) =>
        data.includes(metric.id)
      );
      if (isProjectLevel) {
        const payload = {
          project_id: this.projectId,
          metric_id: data,
        };
        this.dataLoaded = false;
        this.scorecardLoading.emit(false);
        this.scorecardService.addProjectLevelMetrics(payload).subscribe(() => {
          this.toastrService.success("Metrics added successfully");
          getScoreCardQuadrantData.bind(this)();
        });
      }
    }
  });
}

export function handleDeleteWidget(this: any, widget: any) {
  const dialogRef = this.dialog.open(DeletePopupComponent, {
    data: widget.name,
    disableClose: true,
    autoFocus: false,
  });
  dialogRef.afterClosed().subscribe((result: any) => {
    if (result) {
      this.scorecardService.deleteWidget(widget.id).subscribe((res: any) => {
        this.toastrService.success("Widget updated successfully");
        setTimeout(() => {
          this.navigateBack();
        }, 500);
      });
    }
  });
}

export function handleWidgetUpdation(this: any) {
  let payload = this.widgetCreateForm.value;
  payload["metric_ids"] = this.selectedMetrics.map((metric: any) => metric.id);
  this.widgetsLoaded = false;
  this.scorecardService
    .updateWidget(this.currentWidget.id, payload)
    .subscribe(() => {
      this.toastrService.success("Widget updated successfully");
      this.widgetsLoaded = true;
      setTimeout(() => {
        this.navigateBack();
      }, 500);
    });
}

export function initPerformanceWidgets(this: any) {
  this.isCreateWidget = false;
  this.isWidgetInfo = false;
  this.currentWidget = null;
  this.showTrendView = true;
  this.widgetTrendData = [];
  this.trendTableColumns = [];
  this.isWidgetEditMode = false;
  this.showRecommendedWidgets = false;
  this.selectedMetrics = [];
  this.widgetCreateForm.reset();
}

export function handleWidgetClone(this: any) {
  const payload = {
    project_id: this.projectId,
    widget_id: this.currentWidget.id,
  };
  this.scorecardService.cloneWidget(payload).subscribe((res: any) => {
    this.toastrService.success("Widget cloned successfully");
    setTimeout(() => {
      this.navigateBack();
    }, 500);
  });
}

export function handleWidgetEdit(this: any, widget: any) {
  this.isCreateWidget = true;
  this.isWidgetEditMode = true;
  this.currentWidget = widget;
  this.widgetCreateForm.patchValue({
    name: widget.name,
    description: widget.description,
  });
  if (this.showRecommendedWidgets) {
    this.widgetCreateForm.disable();
  }
  this.scorecardService.getWidget(widget.id).subscribe((res: any) => {
    this.selectedMetrics = res.metrics;
  });
}

function getMetricValue(metric: any) {
  const unitName = metric.metric_unit_name;
  if (unitName) {
    return `(${unitName})`;
  } else {
    return "";
  }
}

export function getTrendChartData(trendData: any, freq: any) {
  let categories: string[] = [];
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "June",
    "July",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const metric_data = trendData.metrics[0]?.metric_data;

  if (freq === "daily") {
    categories = metric_data.map((data: any) => {
      const [_, month, date] = data.period.split("-").map(Number);
      return `${monthNames[month - 1]} ${date}`;
    });
  } else if (freq === "weekly") {
    categories = metric_data.map((data: any) => data.period);
  } else {
    categories = metric_data.map((data: any) => {
      const [year, month] = data.period.split("-").map(Number);
      return `${monthNames[month - 1]} ${year}`;
    });
  }

  const seriesData = trendData.metrics.map((metric: any) => ({
    name: metric.display_name + getMetricValue(metric),
    data: metric.metric_data.map((data: any) => data.value),
  }));
  return {
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    title: {
      text: "",
    },
    yAxis: {
      title: {
        text: "Value",
      },
    },
    xAxis: {
      categories,
      labels: { rotation: -30 },
      accessibility: {
        rangeDescription: "Frequency",
      },
    },
    series: seriesData,
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
          },
        },
      ],
    },
  };
}

export function getPointInTimeChartData(pointInTimeData: any) {
  let gaugeChartOptions: any = {
    chart: {
      type: "solidgauge",
    },
    tooltip: {
      enabled: true,
    },
    pane: {
      startAngle: -90,
      endAngle: 90,
      background: null,
      center: ["50%", "75%"],
      size: "110%",
    },
    credits: {
      enabled: false,
    },
    yAxis: {},
  };

  const metrics = pointInTimeData?.metrics;
  let allChartOptions: any = [];
  for (let metric of metrics) {
    const metric_unit_name = metric?.metric_unit_name
      ? "(" + metric.metric_unit_name + ")"
      : "";
    const metric_data: any = metric.metric_data;
    let min_value = 0;
    let max_value = 100;
    let chartOptions: any = { ...gaugeChartOptions };
    if (metric_data[0]?.value) {
      chartOptions.series = [
        {
          name: metric.display_name,
          data: [parseFloat(metric_data[0].value)],
          dataLabels: {
            enabled: false,
          },
        },
      ];
      chartOptions.subtitle = {
        enabled: true,
        text: " Current Value: " + metric_data[0].value,
      };
      chartOptions.pane = {
        ...chartOptions.pane,
        background: {
          backgroundColor: "#fafafa",
          borderRadius: 5,
          innerRadius: "60%",
          outerRadius: "100%",
          shape: "arc",
        },
      };

      // setting min_value & max value if min_value & max_value is null for matrix
      if (metric.min_value != null) {
        min_value = parseFloat(metric.min_value);
      }

      if (metric.max_value != null) {
        max_value = parseFloat(metric.max_value);
      } else {
        max_value = parseFloat(metric_data[0].value);
      }
    } else {
      chartOptions.series = [];
    }
    chartOptions = {
      ...chartOptions,
      title: {
        enabled: true,
        text: metric.display_name + " " + metric_unit_name,
      },
      yAxis: {
        min: min_value,
        max: max_value,
        tickColor: "#00000000",
        minorTickInterval: null,
        lineWidth: 0,
        labels: {
          distance: 20,
          style: {
            fontSize: "14px",
          },
        },
      },
    };
    allChartOptions.push(chartOptions);
  }
  return allChartOptions;
}
